import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { Plus, Pencil, Trash2, Upload, Eye, ExternalLink, RotateCcw, Image as ImageIcon, ArrowUp, ArrowDown, X } from "lucide-react";
import { API } from "../../App";

const emptyForm = {
  slug: "",
  title: "",
  excerpt: "",
  body_html: "",
  cover_image: "",
  tags: "",
  meta_description: "",
  meta_keywords: "",
  service_slug: "",
  published: true,
  gallery: [], // [{image_id, url, alt, caption}]
};

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const BlogAdminTab = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null); // null = creating
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [seedRunning, setSeedRunning] = useState(false);

  const backendBase = useMemo(() => process.env.REACT_APP_BACKEND_URL || "", []);
  const coverImageUrl = (relOrAbs) => {
    if (!relOrAbs) return null;
    if (/^https?:\/\//i.test(relOrAbs)) return relOrAbs;
    return `${backendBase}${relOrAbs}`;
  };

  const loadArticles = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/blog/admin/list`);
      setArticles(data.articles || []);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArticles();
  }, []);

  const openCreate = () => {
    setEditingSlug(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (article) => {
    setEditingSlug(article.slug);
    setForm({
      slug: article.slug || "",
      title: article.title || "",
      excerpt: article.excerpt || "",
      body_html: article.body_html || "",
      cover_image: article.cover_image || "",
      tags: (article.tags || []).join(", "),
      meta_description: article.meta_description || "",
      meta_keywords: article.meta_keywords || "",
      service_slug: article.service_slug || "",
      published: article.published !== false,
      gallery: Array.isArray(article.gallery) ? article.gallery : [],
    });
    setShowForm(true);
  };

  const handleTitleChange = (v) => {
    // Auto-derive slug when creating a new article and slug is still blank/untouched
    setForm((prev) => {
      const next = { ...prev, title: v };
      if (editingSlug === null && (!prev.slug || prev.slug === slugify(prev.title))) {
        next.slug = slugify(v);
      }
      return next;
    });
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/blog/admin/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, cover_image: data.url }));
      toast.success("Cover image uploaded");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const uploads = await Promise.all(
        Array.from(files).map(async (file) => {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await axios.post(`${API}/blog/admin/upload-image`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          return { image_id: data.image_id, url: data.url, alt: "", caption: "" };
        })
      );
      setForm((prev) => ({ ...prev, gallery: [...(prev.gallery || []), ...uploads] }));
      toast.success(`${uploads.length} image${uploads.length > 1 ? "s" : ""} added to gallery`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Gallery upload failed");
    } finally {
      setUploading(false);
    }
  };

  const updateGalleryItem = (idx, patch) => {
    setForm((prev) => {
      const g = [...(prev.gallery || [])];
      g[idx] = { ...g[idx], ...patch };
      return { ...prev, gallery: g };
    });
  };

  const removeGalleryItem = (idx) => {
    setForm((prev) => ({ ...prev, gallery: (prev.gallery || []).filter((_, i) => i !== idx) }));
  };

  const moveGalleryItem = (idx, dir) => {
    setForm((prev) => {
      const g = [...(prev.gallery || [])];
      const j = idx + dir;
      if (j < 0 || j >= g.length) return prev;
      [g[idx], g[j]] = [g[j], g[idx]];
      return { ...prev, gallery: g };
    });
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.body_html.trim()) return toast.error("Body content is required");
    setSaving(true);
    const payload = {
      slug: form.slug ? slugify(form.slug) : slugify(form.title),
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      body_html: form.body_html,
      cover_image: form.cover_image || null,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      meta_description: form.meta_description || form.excerpt,
      meta_keywords: form.meta_keywords || null,
      service_slug: form.service_slug || null,
      published: form.published,
      gallery: (form.gallery || []).map(g => ({
        image_id: g.image_id || null,
        url: g.url,
        alt: (g.alt || "").trim(),
        caption: (g.caption || "").trim() || null,
      })).filter(g => g.url),
    };
    try {
      if (editingSlug) {
        await axios.put(`${API}/blog/admin/${editingSlug}`, payload);
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/blog/admin`, payload);
        toast.success("Article created");
      }
      setShowForm(false);
      loadArticles();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/blog/admin/${deleteConfirm.slug}`);
      toast.success("Article deleted");
      setDeleteConfirm(null);
      loadArticles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const runSeed = async () => {
    setSeedRunning(true);
    try {
      const { data } = await axios.post(`${API}/blog/admin/seed`);
      toast.success(`Seed complete: ${data.inserted.length} inserted, ${data.skipped.length} skipped`);
      loadArticles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally {
      setSeedRunning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="blog-admin-tab">
      {/* Header */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <CardTitle className="text-white flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-saturn-orange" /> Blog & Guides
            </CardTitle>
            <p className="text-slate-400 text-sm mt-1">
              Publish SEO-rich service guides that show on <span className="text-orange-400">/blog</span> and the homepage.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:flex-shrink-0">
            <Button
              variant="outline"
              onClick={runSeed}
              disabled={seedRunning}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="blog-admin-seed-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {seedRunning ? "Seeding…" : "Re-seed"}
            </Button>
            <Button
              onClick={openCreate}
              className="bg-saturn-orange hover:bg-saturn-orange-hover text-white"
              data-testid="blog-admin-new-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Article
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-400 text-center py-8" data-testid="blog-admin-loading">Loading…</p>
          ) : articles.length === 0 ? (
            <p className="text-slate-400 text-center py-8" data-testid="blog-admin-empty">
              No articles yet. Click <strong>New Article</strong> to publish your first guide.
            </p>
          ) : (
            <div className="grid gap-4">
              {articles.map((a) => (
                <div
                  key={a.slug}
                  className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-slate-900/40 border border-slate-800 rounded-lg"
                  data-testid={`blog-admin-row-${a.slug}`}
                >
                  {/* Thumb */}
                  <div className="w-full sm:w-24 h-24 rounded-md overflow-hidden bg-slate-800 flex items-center justify-center flex-shrink-0">
                    {a.cover_image ? (
                      <img src={coverImageUrl(a.cover_image)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-slate-600" />
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-white font-semibold truncate">{a.title}</h3>
                      {a.published ? (
                        <Badge className="bg-green-600/20 text-green-300 border border-green-500/30 text-[10px]">PUBLISHED</Badge>
                      ) : (
                        <Badge className="bg-yellow-600/20 text-yellow-300 border border-yellow-500/30 text-[10px]">DRAFT</Badge>
                      )}
                      {typeof a.views === "number" && (
                        <span className="text-xs text-slate-400 inline-flex items-center gap-1">
                          <Eye className="w-3 h-3" /> {a.views}
                        </span>
                      )}
                    </div>
                    <p className="text-slate-400 text-xs truncate">/blog/{a.slug}</p>
                    {a.excerpt && <p className="text-slate-500 text-xs mt-1 line-clamp-2">{a.excerpt}</p>}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      onClick={() => window.open(`/blog/${a.slug}`, "_blank")}
                      data-testid={`blog-admin-view-${a.slug}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      onClick={() => openEdit(a)}
                      data-testid={`blog-admin-edit-${a.slug}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-800 text-red-400 hover:bg-red-950/40"
                      onClick={() => setDeleteConfirm(a)}
                      data-testid={`blog-admin-delete-${a.slug}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl bg-slate-900 border-slate-700 text-white max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSlug ? "Edit Article" : "New Article"}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingSlug ? "Update this guide's content, SEO or cover image." : "Publish a new SEO-rich guide to /blog."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 mt-2">
            {/* Title + slug */}
            <div>
              <Label className="text-slate-300">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="e.g. Window Tinting in Ireland — The Complete Guide"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="blog-form-title"
              />
            </div>
            <div>
              <Label className="text-slate-300">URL Slug</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="auto-generated from title"
                className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                data-testid="blog-form-slug"
              />
              <p className="text-xs text-slate-500 mt-1">Article will live at <span className="text-orange-400">/blog/{form.slug || slugify(form.title) || "…"}</span></p>
            </div>

            {/* Cover image */}
            <div>
              <Label className="text-slate-300">Cover Image</Label>
              <div className="flex items-start gap-3">
                <div className="w-32 h-24 rounded-md overflow-hidden bg-slate-800 flex items-center justify-center border border-slate-700 flex-shrink-0">
                  {form.cover_image ? (
                    <img src={coverImageUrl(form.cover_image)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-slate-600" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer bg-slate-800 hover:bg-slate-700 text-white px-3 py-2 rounded-md text-sm border border-slate-700" data-testid="blog-form-upload-label">
                    <Upload className="w-4 h-4" />
                    <span>{uploading ? "Uploading…" : "Upload image"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploading}
                      onChange={(e) => handleUpload(e.target.files?.[0])}
                      data-testid="blog-form-upload-input"
                    />
                  </label>
                  <Input
                    value={form.cover_image || ""}
                    onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                    placeholder="…or paste a URL (or leave blank)"
                    className="bg-slate-800 border-slate-700 text-white text-xs"
                    data-testid="blog-form-cover-url"
                  />
                  {form.cover_image && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      onClick={() => setForm({ ...form, cover_image: "" })}
                      data-testid="blog-form-clear-cover"
                    >
                      Remove image
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Excerpt */}
            <div>
              <Label className="text-slate-300">Short Excerpt</Label>
              <Textarea
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                placeholder="1–2 sentence summary shown on cards and used as fallback meta description"
                rows={2}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="blog-form-excerpt"
              />
            </div>

            {/* Body */}
            <div>
              <Label className="text-slate-300">Body (HTML)</Label>
              <Textarea
                value={form.body_html}
                onChange={(e) => setForm({ ...form, body_html: e.target.value })}
                placeholder="&lt;h2&gt;Section title&lt;/h2&gt;&#10;&lt;p&gt;Paragraph…&lt;/p&gt;&#10;&lt;ul&gt;&lt;li&gt;Point&lt;/li&gt;&lt;/ul&gt;"
                rows={14}
                className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                data-testid="blog-form-body"
              />
              <p className="text-xs text-slate-500 mt-1">Use standard HTML tags: <code className="text-orange-300">h2</code>, <code className="text-orange-300">p</code>, <code className="text-orange-300">ul</code>, <code className="text-orange-300">strong</code>, <code className="text-orange-300">a</code>. Link to <code className="text-orange-300">/blog/other-slug</code> or <code className="text-orange-300">/book</code>.</p>
            </div>

            {/* Gallery */}
            <div className="border-t border-slate-800 pt-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-slate-300 text-base">Photo Gallery</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Add before/after photos — alt text is used by Google Images (huge SEO boost)</p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer bg-slate-800 hover:bg-slate-700 text-white px-3 py-2 rounded-md text-sm border border-slate-700" data-testid="blog-form-gallery-upload-label">
                  <Upload className="w-4 h-4" />
                  <span>{uploading ? "Uploading…" : "Add photos"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => handleGalleryUpload(e.target.files)}
                    data-testid="blog-form-gallery-upload-input"
                  />
                </label>
              </div>
              {(form.gallery || []).length === 0 ? (
                <div className="text-center text-slate-500 text-sm py-6 border border-dashed border-slate-700 rounded-md">
                  No gallery images yet. Click <strong>Add photos</strong> to upload one or several at once.
                </div>
              ) : (
                <div className="grid gap-3">
                  {(form.gallery || []).map((g, idx) => (
                    <div key={`${g.url}-${idx}`} className="flex items-start gap-3 p-3 bg-slate-800/40 border border-slate-700 rounded-md" data-testid={`blog-form-gallery-item-${idx}`}>
                      <img src={coverImageUrl(g.url)} alt={g.alt || ""} className="w-24 h-24 object-cover rounded flex-shrink-0" />
                      <div className="flex-1 space-y-2 min-w-0">
                        <Input
                          value={g.alt || ""}
                          onChange={(e) => updateGalleryItem(idx, { alt: e.target.value })}
                          placeholder="Alt text (describe the image — e.g. 'BMW 3 Series with 20% ceramic tint on all rear windows')"
                          className="bg-slate-900 border-slate-700 text-white text-sm"
                          data-testid={`blog-form-gallery-alt-${idx}`}
                        />
                        <Input
                          value={g.caption || ""}
                          onChange={(e) => updateGalleryItem(idx, { caption: e.target.value })}
                          placeholder="Optional caption (shown under the image on the article page)"
                          className="bg-slate-900 border-slate-700 text-white text-sm"
                          data-testid={`blog-form-gallery-caption-${idx}`}
                        />
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <Button
                          size="icon"
                          variant="outline"
                          disabled={idx === 0}
                          onClick={() => moveGalleryItem(idx, -1)}
                          className="border-slate-600 text-slate-300 hover:bg-slate-700 h-7 w-7"
                          data-testid={`blog-form-gallery-up-${idx}`}
                          title="Move up"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          disabled={idx === (form.gallery || []).length - 1}
                          onClick={() => moveGalleryItem(idx, 1)}
                          className="border-slate-600 text-slate-300 hover:bg-slate-700 h-7 w-7"
                          data-testid={`blog-form-gallery-down-${idx}`}
                          title="Move down"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() => removeGalleryItem(idx)}
                          className="border-red-800 text-red-400 hover:bg-red-950/40 h-7 w-7"
                          data-testid={`blog-form-gallery-remove-${idx}`}
                          title="Remove"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tags + service slug */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Tags (comma-separated)</Label>
                <Input
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="window tint, Ireland, aftercare"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="blog-form-tags"
                />
              </div>
              <div>
                <Label className="text-slate-300">Linked Service (slug)</Label>
                <Input
                  value={form.service_slug}
                  onChange={(e) => setForm({ ...form, service_slug: e.target.value })}
                  placeholder="e.g. window-tint (optional)"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="blog-form-service-slug"
                />
              </div>
            </div>

            {/* SEO */}
            <div>
              <Label className="text-slate-300">Meta Description</Label>
              <Textarea
                value={form.meta_description}
                onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                placeholder="150–160 characters — this shows in Google search results (falls back to excerpt)"
                rows={2}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="blog-form-meta-desc"
              />
            </div>
            <div>
              <Label className="text-slate-300">Meta Keywords</Label>
              <Input
                value={form.meta_keywords}
                onChange={(e) => setForm({ ...form, meta_keywords: e.target.value })}
                placeholder="car tinting Ireland, window tint Meath, ceramic tint Dublin"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="blog-form-meta-keywords"
              />
            </div>

            {/* Published toggle */}
            <div className="flex items-center justify-between p-3 rounded-md bg-slate-800/60 border border-slate-700">
              <div>
                <Label className="text-slate-200">Published</Label>
                <p className="text-xs text-slate-500">Turn off to save as a draft (won&apos;t appear on /blog)</p>
              </div>
              <Switch
                checked={form.published}
                onCheckedChange={(v) => setForm({ ...form, published: v })}
                data-testid="blog-form-published"
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="blog-form-cancel">
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving} className="bg-saturn-orange hover:bg-saturn-orange-hover text-white" data-testid="blog-form-save">
              {saving ? "Saving…" : editingSlug ? "Update Article" : "Publish Article"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Delete article?</DialogTitle>
            <DialogDescription className="text-slate-400">
              This will permanently remove &ldquo;{deleteConfirm?.title}&rdquo;. This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="blog-admin-delete-cancel">
              Cancel
            </Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="blog-admin-delete-confirm">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogAdminTab;
